/**
 * **One media at a time** — the single “playback slot” of the whole page.
 *
 * The community feed can show many players at once (auto-playing videos, reels,
 * voice notes, mp3 posts, chat bubbles). Each media element used to start on its
 * own, so scrolling could leave two clips playing over each other and a second
 * mp3 could start while the first was still talking.
 *
 * Every player asks for the slot before it starts and hands back a **stop
 * callback** — that is what makes it work for an `<video>` (pause it) *and* for
 * an embed (`<iframe>` — swap it back to the preview so the platform player is
 * unmounted). Claiming the slot stops the previous owner immediately, so:
 *
 *   · scrolling to a video stops whatever played before (video **or** mp3),
 *   · an mp3 only ever starts from a real click (see `AudioPlayer`),
 *   · two players can never talk at the same time.
 *
 * `utils/videoSound.js` re-exports thin wrappers over this module so the older
 * call sites (`claimPlayback(el)` / `releasePlayback(el)`) keep working.
 */
import { useSyncExternalStore } from 'react';

const EVENT = 'ms-media-playback';

let ownerId = null;
let ownerStop = null;
const listeners = new Set();

function emit() {
  listeners.forEach((fn) => { try { fn(); } catch (e) { /* ignore */ } });
  if (typeof window !== 'undefined' && window.dispatchEvent) {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { id: ownerId } }));
  }
}

/** Who owns the slot right now (an element or any unique object). */
export function currentPlaybackId() {
  return ownerId;
}

/**
 * Take the playback slot. The previous owner's stop callback runs at once, so
 * it is already silent/paused before the new media starts.
 */
export function requestPlayback(id, stop) {
  if (id == null) return;
  if (ownerId === id) {
    ownerStop = stop || ownerStop;               // same player: just refresh the stopper
    return;
  }
  const prevStop = ownerStop;
  ownerId = id;
  ownerStop = stop || null;
  if (prevStop) { try { prevStop(); } catch (e) { /* ignore */ } }
  emit();
}

/** Give the slot back (the media paused / the card scrolled away / unmounted). */
export function releasePlayback(id) {
  if (ownerId !== id) return;
  ownerId = null;
  ownerStop = null;
  emit();
}

/**
 * Stop whatever is playing — used when a modal opens over the page.
 * @param {*} [exceptId] keep this owner alive (optional).
 * @returns {boolean} true when something was stopped.
 */
export function stopPlayback(exceptId = null) {
  if (ownerId == null || (exceptId != null && ownerId === exceptId)) return false;
  const stop = ownerStop;
  ownerId = null;
  ownerStop = null;
  if (stop) { try { stop(); } catch (e) { /* ignore */ } }
  emit();
  return true;
}

/** Subscribe to ownership changes (used by `usePlaybackOwner` / tests). */
export function subscribePlayback(fn) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** `true` while `id` owns the slot — handy for tests and small badges. */
export function usePlaybackOwner(id) {
  const owner = useSyncExternalStore(subscribePlayback, currentPlaybackId, currentPlaybackId);
  return owner === id;
}
